import { Component, OnInit } from '@angular/core';
import { CoreService, ReportChartConfig } from 'src/app/core/helpers/core.service';
import { forkJoin } from 'rxjs';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

// Interface aligned with API response structure
interface FetchTotalResponse {
  messageKey: string;
  result: {
    groupedTotal: { [key: string]: number };
  };
}

interface TrendDataPoint {
  date: string;
  count: number;
  groupedData?: { [key: string]: number };
}

interface TrendResponse {
  messageKey: string;
  result: TrendDataPoint[];
}

interface ProductSelectionData {
  productName: string;
  totalQuantity: number;
  receiptCount: number;
  totalPoints: number;
}

interface ProductTrendResponse {
  messageKey: string;
  result: ProductSelectionData[];
}

enum TotalType {
  registrations = 0,
  marketting_optins = 1,
  codes = 2,
  rewards = 3,
  participations = 4,
  approvedparticipations = 5,
  email_optins = 6
}

enum TrendPeriod {
  Daily = 0,
  Weekly = 1
}

@Component({
  selector: 'loyaltic-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit {

  public pieChartOptInsLabels: string[] = ['Total Users', 'Marketing Opt-Ins', 'Email Opt-Ins'];
  public pieChartRegistrations: number[] = [0, 0, 0];
  public pieChartCodeEntriesLabels: string[] = [];
  public pieChartCodeEntriesValues: number[] = [];
  public barchartRewardLabels: string[] = [];
  public barChartRewardValues: number[] = [];
  public participationsLabels: string[] = [];
  public participationsValues: number[] = [];
  totalUsers = 0;
  isLoading = false;
  pieChartMarketingOptIns = 0;
  pieChartEmailOptIns = 0;
  codeEntriesData: { [key: string]: number } = {};
  rewardsData: { [key: string]: number } = {};
  participationsData: { [key: string]: number } = {};

  // Trend data properties
  public registrationTrendLabels: string[] = [];
  public registrationTrendData: number[] = [];
  public receiptTrendLabels: string[] = [];
  public receiptTrendData: number[] = [];
  public rewardTrendLabels: string[] = [];
  public rewardTrendDatasets: any[] = [];

  // Reward size distribution data
  public rewardSizeLabels: string[] = [];
  public rewardSizeDatasets: any[] = [];

  // Low-stock notification opt-in data
  public rewardNotificationLabels: string[] = [];
  public rewardNotificationValues: number[] = [];

  // Product trends data
  public productTrendLabels: string[] = [];
  public productTrendQuantities: number[] = [];
  public productTrendData: ProductSelectionData[] = [];

  // Weather bonus trend data
  public weatherBonusTrendLabels: string[] = [];
  public weatherBonusTrendData: number[] = [];
  public weatherBonusAppliedCount: number = 0;

  // Loading states for each trend graph
  public isLoadingRegistrationTrend = false;
  public isLoadingReceiptTrend = false;
  public isLoadingRewardTrend = false;
  public isLoadingRewardSizes = false;
  public isLoadingRewardNotifications = false;
  public isLoadingProductTrend = false;
  public isLoadingWeatherBonusTrend = false;

  // Period selection
  public selectedPeriod: TrendPeriod = TrendPeriod.Daily;
  public TrendPeriod = TrendPeriod;

  // Date range filter
  public startDate: string = '';
  public endDate: string = '';

  // Chart config / visibility
  readonly isAdminHost: boolean = /localhost|azurewebsites\.net/.test(globalThis.location.hostname);
  public showConfig: boolean = false;
  public isSavingConfig: boolean = false;
  public chartConfig: ReportChartConfig = {
    showTotalUsers: true,
    showMarketingOptIns: true,
    showEmailOptIns: true,
    showTotalCodes: true,
    showTotalRewards: true,
    showParticipations: true,
    showOptInsChart: true,
    showCodeEntriesChart: true,
    showRewardsChart: true,
    showParticipationsChart: true,
    showSizeDistributionChart: true,
    showNotificationOptInsChart: true,
    showRegistrationTrend: true,
    showReceiptTrend: true,
    showRewardTrend: true,
    showProductTrend: true,
    totalUsersLabel: 'Total Users',
    marketingOptInsLabel: 'Marketing Opt-Ins',
    emailOptInsLabel: 'Email Opt-Ins',
    totalCodesLabel: 'Total Codes',
    totalRewardsLabel: 'Total Rewards',
    participationsLabel: 'Participations'
  };

  // Getters for stat cards
  get totalCodes(): number {
    return this.pieChartCodeEntriesValues.reduce((a, b) => a + b, 0);
  }

  get totalRewards(): number {
    return this.barChartRewardValues.reduce((a, b) => a + b, 0);
  }

  get totalParticipations(): number {
    // Sum all participation values
    return this.participationsValues.reduce((a, b) => a + b, 0);
  }

  // Chart types for different visualizations
  public optInsChartType: string = 'bar';
  public codeEntriesChartType: string = 'pie';
  public rewardsChartType: string = 'horizontalBar';
  public participationsChartType: string = 'bar';
  public trendChartType: string = 'line';
  public rewardTrendChartType: string = 'line';
  public rewardSizeChartType: string = 'bar';
  public rewardNotificationChartType: string = 'horizontalBar';

  // Modern gradient-inspired color palette
  public pieChartColors: Array<any> = [{
    backgroundColor: [
      'rgba(99, 102, 241, 0.8)',   // Indigo
      'rgba(244, 63, 94, 0.8)',    // Rose
      'rgba(168, 85, 247, 0.8)'    // Purple
    ],
    borderColor: [
      'rgba(99, 102, 241, 1)',
      'rgba(244, 63, 94, 1)',
      'rgba(168, 85, 247, 1)'
    ],
    borderWidth: 2
  }];

  public codeEntriesColors: Array<any> = [{
    backgroundColor: [
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(16, 185, 129, 0.8)',   // Emerald
      'rgba(251, 146, 60, 0.8)',   // Orange
      'rgba(139, 92, 246, 0.8)',   // Violet
      'rgba(236, 72, 153, 0.8)',   // Pink
      'rgba(34, 197, 94, 0.8)',    // Green
      'rgba(249, 115, 22, 0.8)',   // Deep Orange
      'rgba(168, 85, 247, 0.8)'    // Purple
    ],
    borderColor: [
      'rgba(59, 130, 246, 1)',
      'rgba(16, 185, 129, 1)',
      'rgba(251, 146, 60, 1)',
      'rgba(139, 92, 246, 1)',
      'rgba(236, 72, 153, 1)',
      'rgba(34, 197, 94, 1)',
      'rgba(249, 115, 22, 1)',
      'rgba(168, 85, 247, 1)'
    ],
    borderWidth: 2
  }];

  public participationsColors: Array<any> = [{
    backgroundColor: [
      'rgba(168, 85, 247, 0.8)',   // Purple
      'rgba(236, 72, 153, 0.8)'    // Pink
    ],
    borderColor: [
      'rgba(168, 85, 247, 1)',
      'rgba(236, 72, 153, 1)'
    ],
    borderWidth: 2
  }];

  public chartColors = [{
    backgroundColor: [
      'rgba(99, 102, 241, 0.8)',   // Indigo
      'rgba(16, 185, 129, 0.8)',   // Emerald
      'rgba(244, 63, 94, 0.8)',    // Rose
      'rgba(251, 146, 60, 0.8)',   // Orange
      'rgba(168, 85, 247, 0.8)',   // Purple
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(236, 72, 153, 0.8)',   // Pink
      'rgba(34, 197, 94, 0.8)',    // Green
      'rgba(249, 115, 22, 0.8)',   // Deep Orange
      'rgba(139, 92, 246, 0.8)',   // Violet
      'rgba(14, 165, 233, 0.8)',   // Sky
      'rgba(234, 88, 12, 0.8)',    // Orange red
      'rgba(168, 162, 158, 0.8)',  // Stone
      'rgba(20, 184, 166, 0.8)',   // Teal
      'rgba(217, 70, 239, 0.8)',   // Fuchsia
      'rgba(132, 204, 22, 0.8)',   // Lime
      'rgba(251, 191, 36, 0.8)',   // Amber
      'rgba(244, 114, 182, 0.8)',  // Light Pink
      'rgba(56, 189, 248, 0.8)',   // Light Blue
      'rgba(134, 239, 172, 0.8)'   // Light Green
    ],
    borderColor: [
      'rgba(99, 102, 241, 1)',
      'rgba(16, 185, 129, 1)',
      'rgba(244, 63, 94, 1)',
      'rgba(251, 146, 60, 1)',
      'rgba(168, 85, 247, 1)',
      'rgba(59, 130, 246, 1)',
      'rgba(236, 72, 153, 1)',
      'rgba(34, 197, 94, 1)',
      'rgba(249, 115, 22, 1)',
      'rgba(139, 92, 246, 1)',
      'rgba(14, 165, 233, 1)',
      'rgba(234, 88, 12, 1)',
      'rgba(168, 162, 158, 1)',
      'rgba(20, 184, 166, 1)',
      'rgba(217, 70, 239, 1)',
      'rgba(132, 204, 22, 1)',
      'rgba(251, 191, 36, 1)',
      'rgba(244, 114, 182, 1)',
      'rgba(56, 189, 248, 1)',
      'rgba(134, 239, 172, 1)'
    ],
    borderWidth: 2
  }];


  // Options for Doughnut Chart (Opt-Ins)
  public doughnutChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        fontColor: '#333',
        fontSize: 13,
        padding: 15,
        usePointStyle: true
      }
    },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6,
      displayColors: true,
      callbacks: {
        label: function(tooltipItem: any, data: any) {
          const label = data.labels[tooltipItem.index] || '';
          const value = data.datasets[0].data[tooltipItem.index];
          const total = data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    },
    cutoutPercentage: 65,
    animation: {
      animateScale: true,
      animateRotate: true
    }
  };

  // Options for Pie Charts (Code Entries)
  public pieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        fontColor: '#333',
        fontSize: 12,
        padding: 12,
        usePointStyle: true,
        boxWidth: 15
      }
    },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6,
      displayColors: true,
      callbacks: {
        label: function(tooltipItem: any, data: any) {
          const label = data.labels[tooltipItem.index] || '';
          const value = data.datasets[0].data[tooltipItem.index];
          const total = data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    },
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Options for Bar Charts (Participations)
  public barChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6,
      callbacks: {
        label: function(tooltipItem: any, data: any) {
          const label = data.labels[tooltipItem.index] || '';
          const value = tooltipItem.yLabel;
          return `${label}: ${value}`;
        }
      }
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          fontColor: '#666',
          fontSize: 12
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: false,
          fontColor: '#666',
          fontSize: 11,
          maxRotation: 45,
          minRotation: 0
        },
        gridLines: {
          display: false
        }
      }]
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Options for grouped Bar Charts with multiple datasets (e.g. Reward Size Distribution) — same as
  // barChartOptions but with the legend enabled so each dataset's color (size) is identifiable.
  public groupedBarChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        fontColor: '#333',
        fontSize: 12,
        padding: 12,
        usePointStyle: true,
        boxWidth: 15
      }
    },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6,
      mode: 'index',
      intersect: false
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          fontColor: '#666',
          fontSize: 12
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: false,
          fontColor: '#666',
          fontSize: 11,
          maxRotation: 45,
          minRotation: 0
        },
        gridLines: {
          display: false
        }
      }]
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Options for Horizontal Bar Charts (Rewards)
  public horizontalBarOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6,
      callbacks: {
        label: (tooltipItem: any, data: any) => {
          const label = data.labels[tooltipItem.index] || '';
          const value = tooltipItem.xLabel;
          const dataset = data.datasets[0];
          const total = dataset.data.reduce((acc: number, val: number) => acc + val, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    },
    scales: {
      xAxes: [{
        ticks: {
          beginAtZero: true,
          fontColor: '#666',
          fontSize: 12
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      }],
      yAxes: [{
        ticks: {
          fontColor: '#666',
          fontSize: 11,
          callback: (value: any, index: number, values: any) => {
            // Add count and percentage to y-axis labels
            const dataset = this.barChartRewardValues;
            if (dataset && dataset.length > 0) {
              const total = dataset.reduce((acc, val) => acc + val, 0);
              const itemValue = dataset[index];
              const percentage = ((itemValue / total) * 100).toFixed(1);
              return `${value} (${itemValue}, ${percentage}%)`;
            }
            return value;
          }
        },
        gridLines: {
          display: false
        }
      }]
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Options for the Notification Opt-Ins horizontal bar — same as horizontalBarOptions, but the
  // y-axis callback reads rewardNotificationValues instead of barChartRewardValues.
  public notificationOptInsBarOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6,
      callbacks: {
        label: (tooltipItem: any, data: any) => {
          const label = data.labels[tooltipItem.index] || '';
          const value = tooltipItem.xLabel;
          const dataset = data.datasets[0];
          const total = dataset.data.reduce((acc: number, val: number) => acc + val, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    },
    scales: {
      xAxes: [{
        ticks: {
          beginAtZero: true,
          fontColor: '#666',
          fontSize: 12
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      }],
      yAxes: [{
        ticks: {
          fontColor: '#666',
          fontSize: 11,
          callback: (value: any, index: number, values: any) => {
            const dataset = this.rewardNotificationValues;
            if (dataset && dataset.length > 0) {
              const total = dataset.reduce((acc, val) => acc + val, 0);
              const itemValue = dataset[index];
              const percentage = ((itemValue / total) * 100).toFixed(1);
              return `${value} (${itemValue}, ${percentage}%)`;
            }
            return value;
          }
        },
        gridLines: {
          display: false
        }
      }]
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Options for Line Charts (Trends)
  public lineChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6,
      callbacks: {
        label: function(tooltipItem: any, data: any) {
          const label = data.datasets[0].label || '';
          const value = tooltipItem.yLabel;
          return `${label}: ${value}`;
        }
      }
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          fontColor: '#666',
          fontSize: 12
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
          fontColor: '#666',
          fontSize: 11,
          maxRotation: 45,
          minRotation: 0
        },
        gridLines: {
          display: false
        }
      }]
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 3
      },
      point: {
        radius: 4,
        hitRadius: 10,
        hoverRadius: 6
      }
    }
  };

  // Options for Multi-line Charts (Reward Trends by Product)
  public multiLineChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        fontColor: '#333',
        fontSize: 12,
        padding: 12,
        usePointStyle: true,
        boxWidth: 15
      }
    },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6,
      mode: 'index',
      intersect: false
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          stepSize: 1, 
          fontColor: '#666',
          fontSize: 12
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      }],
      xAxes: [{
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
          fontColor: '#666',
          fontSize: 11,
          maxRotation: 45,
          minRotation: 0
        },
        gridLines: {
          display: false
        }
      }]
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 2
      },
      point: {
        radius: 3,
        hitRadius: 10,
        hoverRadius: 5
      }
    }
  };

  optInsData: any;
  totalData: any;

  constructor(private readonly coreService: CoreService) { }

  ngOnInit() {
    this.coreService.getReportChartConfig().subscribe({
      next: (config) => { this.chartConfig = config; },
      error: () => {}
    });
    this.loadAllData();
    this.loadTrendData();
    this.loadProductTrends();
    this.loadRewardSizeDistribution();
    this.loadRewardNotificationOptIns();
  }

  saveChartConfig(): void {
    this.isSavingConfig = true;
    this.coreService.updateReportChartConfig(this.chartConfig).subscribe({
      next: (config) => { this.chartConfig = config; this.isSavingConfig = false; },
      error: () => { this.isSavingConfig = false; }
    });
  }

  loadAllData() {
    this.isLoading = true;
    const start = this.startDate || undefined;
    const end = this.endDate || undefined;

    // Fetch all data in parallel using RxJS 6 syntax
    forkJoin([
      this.coreService.fetchReport(TotalType.registrations, start, end),
      this.coreService.fetchReport(TotalType.marketting_optins, start, end),
      this.coreService.fetchReport(TotalType.codes, start, end),
      this.coreService.fetchReport(TotalType.rewards, start, end),
      this.coreService.fetchReport(TotalType.approvedparticipations, start, end),
      this.coreService.fetchReport(TotalType.email_optins, start, end)
    ]).subscribe(
      (responses: any) => {
        const [registrationsRes, marketingOptInsRes, codesRes, rewardsRes, participationsRes, emailOptInsRes] = responses as FetchTotalResponse[];
        // Process registrations
        this.totalUsers = registrationsRes.result.groupedTotal['registrations'] || 0;

        // Process marketing & email opt-ins
        this.pieChartMarketingOptIns = marketingOptInsRes.result.groupedTotal['marketting_optins'] || 0;
        this.pieChartEmailOptIns = emailOptInsRes.result.groupedTotal['email_optins'] || 0;
        this.calculateOptInsChart();

        // Process code entries
        const codeGroupedTotal = codesRes.result.groupedTotal;
        const codeSortedEntries = Object.entries(codeGroupedTotal).sort((a, b) => (b[1] as number) - (a[1] as number));
        this.pieChartCodeEntriesLabels = codeSortedEntries.map(([key]) => key);
        this.pieChartCodeEntriesValues = codeSortedEntries.map(([, value]) => value as number);
        this.codeEntriesData = codeGroupedTotal;

        // Process rewards
        const rewardsGroupedTotal = rewardsRes.result.groupedTotal;
        const rewardsSortedEntries = Object.entries(rewardsGroupedTotal)
          .filter(([key]) => key !== 'TotalRewards')
          .sort((a, b) => (b[1] as number) - (a[1] as number));
        this.barchartRewardLabels = rewardsSortedEntries.map(([key]) => key);
        this.barChartRewardValues = rewardsSortedEntries.map(([, value]) => value as number);
        this.rewardsData = rewardsGroupedTotal;

        // Process participations from /api/v1/fetchtotal/5 (auto-approved participations)
        const participationsGroupedTotal = participationsRes.result.groupedTotal;
        const participationsSortedEntries = Object.entries(participationsGroupedTotal)
          .sort((a, b) => (b[1] as number) - (a[1] as number));
        this.participationsLabels = participationsSortedEntries.map(([key]) => key);
        this.participationsValues = participationsSortedEntries.map(([, value]) => value as number);
        this.participationsData = participationsGroupedTotal;

        console.log('All data loaded successfully');
        console.log('Participations from /api/v1/fetchtotal/5:', participationsGroupedTotal);
        console.log('Participation Labels:', this.participationsLabels);
        console.log('Participation Values:', this.participationsValues);

        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading data:', error);
        this.isLoading = false;
      }
    );
  }

  calculateOptInsChart() {
    this.pieChartOptInsLabels = ['Total Users', 'Marketing Opt-Ins', 'Email Opt-Ins'];
    this.pieChartRegistrations = [this.totalUsers, this.pieChartMarketingOptIns, this.pieChartEmailOptIns];
  }

  exportToExcel(jsonData: any[], fileName: string): void {
    // Step 1: Convert JSON to worksheet
    const data = Array.isArray(jsonData) ? jsonData : [jsonData];
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    // Step 2: Create a workbook and add the worksheet
    const workbook: XLSX.WorkBook = {
      Sheets: { 'data': worksheet },
      SheetNames: ['data']
    };

    // Step 3: Convert workbook to Excel buffer
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Step 4: Save the file
    this.saveAsExcelFile(excelBuffer, fileName);
    

    
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: 'application/octet-stream' });
    FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + '.xlsx');
  }

  downloadOptInsExcel() {
    const data = [
      {
        'Total Users': this.totalUsers,
        'Marketing Opt-Ins': this.pieChartMarketingOptIns,
        'Email Opt-Ins': this.pieChartEmailOptIns
      }
    ];
    this.exportToExcel(data, 'Users');
  }

  downloadCodeEntriesExcel() {
    // Convert dictionary to array of objects for Excel export
    const data = Object.entries(this.codeEntriesData).map(([key, value]) => ({
      'Code Type': key,
      'Count': value
    }));
    this.exportToExcel(data, 'Code_Entries');
  }

  downloadRewardsExcel() {
    // Convert dictionary to array of objects for Excel export
    const data = Object.entries(this.rewardsData).map(([key, value]) => ({
      'Reward Type': key,
      'Count': value
    }));
    this.exportToExcel(data, 'Rewards');
  }

  downloadParticipationsExcel() {
    // Convert dictionary to array of objects for Excel export
    const data = Object.entries(this.participationsData).map(([key, value]) => ({
      'Participation Type': key,
      'Count': value
    }));
    this.exportToExcel(data, 'Participations');
  }

  applyDateFilter() {
    this.loadAllData();
    this.loadTrendData();
    this.loadProductTrends();
  }

  clearDateFilter() {
    this.startDate = '';
    this.endDate = '';
    this.loadAllData();
    this.loadTrendData();
    this.loadProductTrends();
  }

  loadTrendData() {
    const start = this.startDate || undefined;
    const end = this.endDate || undefined;

    // Load each trend independently so they don't wait for each other

    // Load registration trends
    this.isLoadingRegistrationTrend = true;
    this.coreService.getRegistrationTrends(start, end, this.selectedPeriod).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.processRegistrationTrends(response.result);
          console.log('Registration trends loaded successfully');
        }
        this.isLoadingRegistrationTrend = false;
      },
      (error) => {
        console.error('Error loading registration trends:', error);
        this.isLoadingRegistrationTrend = false;
      }
    );

    // Load receipt trends
    this.isLoadingReceiptTrend = true;
    this.coreService.getReceiptTrends(start, end, this.selectedPeriod).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.processReceiptTrends(response.result);
          console.log('Receipt trends loaded successfully');
        }
        this.isLoadingReceiptTrend = false;
      },
      (error) => {
        console.error('Error loading receipt trends:', error);
        this.isLoadingReceiptTrend = false;
      }
    );

    // Load reward trends
    this.isLoadingRewardTrend = true;
    this.coreService.getRewardTrends(start, end, this.selectedPeriod).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.processRewardTrends(response.result);
          console.log('Reward trends loaded successfully');
        }
        this.isLoadingRewardTrend = false;
      },
      (error) => {
        console.error('Error loading reward trends:', error);
        this.isLoadingRewardTrend = false;
      }
    );
    
  }

  processRegistrationTrends(trendData: TrendDataPoint[]) {
    this.registrationTrendLabels = trendData.map(dp => this.formatDate(dp.date));
    this.registrationTrendData = trendData.map(dp => dp.count);
  }

  processReceiptTrends(trendData: TrendDataPoint[]) {
    this.receiptTrendLabels = trendData.map(dp => this.formatDate(dp.date));
    this.receiptTrendData = trendData.map(dp => dp.count);
  }

  processRewardTrends(trendData: TrendDataPoint[]) {
    // Extract all dates for labels
    this.rewardTrendLabels = trendData.map(dp => this.formatDate(dp.date));

    // Get all unique products across all data points
    const allProducts = new Set<string>();
    trendData.forEach(dp => {
      if (dp.groupedData) {
        Object.keys(dp.groupedData).forEach(product => allProducts.add(product));
      }
    });

    // Create a dataset for each product
    const productArray = Array.from(allProducts);
    this.rewardTrendDatasets = productArray.map((product, index) => {
      const data = trendData.map(dp => {
        return dp.groupedData && dp.groupedData[product] ? dp.groupedData[product] : 0;
      });

      // Use a color from the chartColors palette
      const colorIndex = index % this.chartColors[0].backgroundColor.length;
      const bgColor = this.chartColors[0].backgroundColor[colorIndex];
      const borderColor = this.chartColors[0].borderColor[colorIndex];

      return {
        label: product,
        data: data,
        backgroundColor: bgColor,
        borderColor: borderColor,
        fill: false
      };
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}`;
  }

  onPeriodChange(period: TrendPeriod) {
    this.selectedPeriod = period;
    this.loadTrendData();
  }

  downloadRegistrationTrendExcel() {
    const data = this.registrationTrendLabels.map((label, index) => ({
      'Date': label,
      'Count': this.registrationTrendData[index]
    }));
    this.exportToExcel(data, 'Registration_Trends');
  }

  downloadReceiptTrendExcel() {
    const data = this.receiptTrendLabels.map((label, index) => ({
      'Date': label,
      'Count': this.receiptTrendData[index]
    }));
    this.exportToExcel(data, 'Receipt_Trends');
  }

  downloadRewardTrendExcel() {
    const data: any[] = [];
    this.rewardTrendLabels.forEach((label, index) => {
      const row: any = { 'Date': label };
      this.rewardTrendDatasets.forEach(dataset => {
        row[dataset.label] = dataset.data[index];
      });
      data.push(row);
    });
    this.exportToExcel(data, 'Reward_Trends');
  }

  processWeatherBonusTrends(trendData: TrendDataPoint[]) {
    this.weatherBonusTrendLabels = trendData.map(dp => this.formatDate(dp.date));
    this.weatherBonusTrendData = trendData.map(dp => dp.count);
  }

  downloadWeatherBonusTrendExcel() {
    const data = this.weatherBonusTrendLabels.map((label, index) => ({
      'Date': label,
      'Rain Bonus Participations': this.weatherBonusTrendData[index]
    }));
    this.exportToExcel(data, 'Rain_Bonus_Participations');
  }

  loadProductTrends() {
    this.isLoadingProductTrend = true;
    this.coreService.getProductTrends(this.startDate || undefined, this.endDate || undefined).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.processProductTrends(response.result);
        }
        this.isLoadingProductTrend = false;
      },
      (error) => {
        console.error('Error loading product trends:', error);
        this.isLoadingProductTrend = false;
      }
    );
  }

  processProductTrends(productData: ProductSelectionData[]) {
    // Store the full data
    this.productTrendData = productData;

    // Take top 20 products by quantity
    const topProducts = productData.slice(0, 20);

    this.productTrendLabels = topProducts.map(p => p.productName);
    this.productTrendQuantities = topProducts.map(p => p.totalQuantity);
  }

  downloadProductTrendExcel() {
    const data = this.productTrendData.map(p => ({
      'Product Name': p.productName,
      'Total Quantity': p.totalQuantity,
      'Receipt Count': p.receiptCount,
      'Total Points': p.totalPoints
    }));
    this.exportToExcel(data, 'Product_Selection_Trends');
  }

  loadRewardSizeDistribution() {
    this.isLoadingRewardSizes = true;
    this.coreService.getRewardSizeDistribution().subscribe(
      (response: any) => {
        if (response && response.rewards) {
          this.processRewardSizeDistribution(response.rewards);
        }
        this.isLoadingRewardSizes = false;
      },
      (error) => {
        console.error('Error loading reward size distribution:', error);
        this.isLoadingRewardSizes = false;
      }
    );
  }

  processRewardSizeDistribution(rewards: { rewardName: string; sizeCounts: { [size: string]: number } }[]) {
    this.rewardSizeLabels = rewards.map(r => r.rewardName);

    // Get all unique sizes across all sized rewards
    const allSizes = new Set<string>();
    rewards.forEach(r => {
      if (r.sizeCounts) {
        Object.keys(r.sizeCounts).forEach(size => allSizes.add(size));
      }
    });

    // Create a dataset for each size
    const sizeArray = Array.from(allSizes);
    this.rewardSizeDatasets = sizeArray.map((size, index) => {
      const data = rewards.map(r => (r.sizeCounts && r.sizeCounts[size]) || 0);
      const colorIndex = index % this.chartColors[0].backgroundColor.length;

      return {
        label: size,
        data,
        backgroundColor: this.chartColors[0].backgroundColor[colorIndex],
        borderColor: this.chartColors[0].borderColor[colorIndex],
        borderWidth: 2
      };
    });
  }

  downloadRewardSizeExcel() {
    const data: any[] = [];
    this.rewardSizeLabels.forEach((label, index) => {
      const row: any = { 'Reward': label };
      this.rewardSizeDatasets.forEach(dataset => {
        row[dataset.label] = dataset.data[index];
      });
      data.push(row);
    });
    this.exportToExcel(data, 'Reward_Size_Distribution');
  }

  loadRewardNotificationOptIns() {
    this.isLoadingRewardNotifications = true;
    this.coreService.getRewardNotificationOptIns().subscribe(
      (response: any) => {
        if (response && response.rewards) {
          const sorted = (response.rewards as { rewardName: string; optInCount: number }[]);
          this.rewardNotificationLabels = sorted.map(r => r.rewardName);
          this.rewardNotificationValues = sorted.map(r => r.optInCount);
        }
        this.isLoadingRewardNotifications = false;
      },
      (error) => {
        console.error('Error loading reward notification opt-ins:', error);
        this.isLoadingRewardNotifications = false;
      }
    );
  }

  downloadRewardNotificationExcel() {
    const data = this.rewardNotificationLabels.map((label, index) => ({
      'Reward': label,
      'Low-Stock Notification Opt-Ins': this.rewardNotificationValues[index]
    }));
    this.exportToExcel(data, 'Reward_Notification_OptIns');
  }
}
